import { useEffect } from "react";
import { t } from "i18next";
import { useAuthStore } from "@/store/auth.store";
import { useUpdateUser } from "@/hooks/user/useUpdateUser";
import { toast } from "@/hooks/use-toast";
import {
  AccountSettingSchema,
  type AccountSettingSchemaType,
} from "@/schemas/accountSettings.schema";

import { ProfileForm } from "./components/ProfileForm";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOccupationsStore } from "@/store/occupations.store";

export const ProfileSettings = (): JSX.Element => {
  const { updateUser, loading, result } = useUpdateUser();
  const { userData, getUser } = useAuthStore();
  const {
    fetchAndSetOccupations,
    loading: loadingOccupations,
    getOccupations,
  } = useOccupationsStore();

  useEffect(() => {
    void fetchAndSetOccupations();
  }, [fetchAndSetOccupations]);

  const form = useForm({
    resolver: zodResolver(AccountSettingSchema),
    shouldUnregister: false,
    reValidateMode: "onSubmit",
    defaultValues: {
      firstname: userData?.firstname || "",
      lastname: userData?.lastname || "",
      phone: userData?.phone || "",
      country: userData?.userPreference.country || "",
      state: userData?.userPreference.state || "",
      city: userData?.userPreference.city || "",
      occupation: userData?.userPreference.occupation || "",
      occupationId: 0,
    },
  });

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("settings.profile.updatedToast.successful.title"),
          description: t(
            "settings.profile.updatedToast.successful.description"
          ),
        });
        if (userData) void getUser(userData.email);
      } else {
        toast({
          variant: "destructive",
          title: t("settings.profile.updatedToast.error.title"),
          description: t("settings.profile.updatedToast.error.description"),
        });
        form.reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading, form]);

  useEffect(() => {
    const formValues: AccountSettingSchemaType = {
      firstname: userData?.firstname || "",
      lastname: userData?.lastname || "",
      phone: userData?.phone || "",
      country: userData?.userPreference.country || "",
      state: userData?.userPreference.state || "",
      city: userData?.userPreference.city || "",
      occupation: userData?.userPreference.occupation || "",
      occupationId: 0,
    };
    form.reset(formValues);
  }, [userData, loading, form]);

  const handleSubmit = (data: Partial<AccountSettingSchemaType>): void => {
    if (userData) {
      void updateUser({ userId: userData.id, ...data });
    }
  };

  return (
    <section>
      <ProfileForm
        form={form}
        loadingOccupations={loadingOccupations}
        occupations={getOccupations()}
        onSubmit={handleSubmit}
      />
    </section>
  );
};
